import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  effect,
  inject,
  input,
} from '@angular/core';
import {
  faChevronRight,
  faFilter,
  faSyncAlt,
} from '@fortawesome/pro-regular-svg-icons';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import {
  FeatureName,
  RoleAction,
  RolePermissionSubject,
} from '@wilson/interfaces';
import { ShiftTimelinePreferredSettingsState } from '@wilson/preferred-settings/state';
import {
  ClearAllUnassignedActivitiesRelatedData,
  ClearAssignmentsFromAllUsers,
  ClearDeterminedShiftActivities,
  ClearShiftActivitiesRows,
  ClearUnassignedShiftsWithoutActivities,
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineUnassignedRegionState,
  AssignedRegionVisibilityStateService,
  TimelineAssignedShiftValidationsStateService,
  TimelineStaysSuggestionService,
  TimelineUnassignedShiftValidationsStateService,
  UpdateWindowWidthHeightToWindowInnerWidthHeight,
} from '@wilson/shift-timeline/state';
import { isValid } from 'date-fns';
import {
  debounceTime,
  filter,
  firstValueFrom,
  Observable,
  Subject,
  Subscription,
  switchMap,
} from 'rxjs';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import { ShiftsIconRenderingHelperService } from '@wilson/shift-timeline/assignments-gantt-chart';

const WINDOW_WIDTH_FOR_COLLAPSE = 2020; // Screen size to check auto collapse should turn on or off
const WINDOW_WIDTH_FOR_COLLAPSE_WHEN_FILTER_OPEN = 2350; // Screen size to check auto collapse action bar when filter drawer open

@Component({
  selector: 'wilson-shift-timeline-header',
  templateUrl: './shift-timeline-header.component.html',
  styleUrls: ['./shift-timeline-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ShiftTimelineHeaderComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  protected timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  private assignedRegionVisibilityStateService = inject(
    AssignedRegionVisibilityStateService,
  );
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly timelineAssignedShiftValidationsStateService = inject(
    TimelineAssignedShiftValidationsStateService,
  );
  private readonly timelineUnassignedShiftValidationsStateService = inject(
    TimelineUnassignedShiftValidationsStateService,
  );
  private readonly shiftsIconRenderingHelperService = inject(
    ShiftsIconRenderingHelperService,
  );
  readonly isFilterDrawerVisible = input.required<boolean>();

  public faSyncAlt = faSyncAlt;
  public faFilter = faFilter;
  public faChevronRight = faChevronRight;
  public isViewOptionCardOpen = true;
  public isActionCardOpen = true;
  public isQuickCreationCardOpen = true;
  public isKPICardOpen = true;

  areActivitiesVisible$ = this.store.select(
    ShiftTimelineSettingsState.areActivitiesVisible,
  );
  isCellView$!: Observable<boolean>;
  FeatureName = FeatureName;
  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;
  private subscription: Subscription = new Subscription();
  private refreshClickSubject = new Subject<void>();

  constructor() {
    effect(() => {
      this.isFilterDrawerVisible();
      this.syncCollapsibleCardsWithFilterDrawer();
    });
  }

  ngOnInit(): void {
    this.isCellView$ = this.store.select(AuthState.userId).pipe(
      filter((userId) => !!userId),
      switchMap((userId) =>
        this.store.select(
          ShiftTimelinePreferredSettingsState.isCellView(userId as string),
        ),
      ),
    );

    if (window.innerWidth < WINDOW_WIDTH_FOR_COLLAPSE) {
      this.isActionCardOpen = false;
      this.isQuickCreationCardOpen = false;
      this.isKPICardOpen = false;
    }

    this.subscription.add(
      this.store
        .select(ShiftTimelineUnassignedRegionState.suggestedShiftTemplates)
        .subscribe((suggestedShiftTemplates) => {
          if (suggestedShiftTemplates.length) {
            this.isActionCardOpen = false;
            this.isKPICardOpen = false;
          }
        }),
    );

    this.subscription.add(
      this.refreshClickSubject
        .pipe(debounceTime(100))
        .subscribe(() => this.refreshTimeline()),
    );
  }

  private syncCollapsibleCardsWithFilterDrawer(): void {
    if (
      this.isFilterDrawerVisible() &&
      window.innerWidth < WINDOW_WIDTH_FOR_COLLAPSE_WHEN_FILTER_OPEN
    ) {
      this.isActionCardOpen = false;
      this.isQuickCreationCardOpen = false;
      this.isKPICardOpen = false;
    } else {
      this.isActionCardOpen = true;
    }
  }

  protected handleRefreshClick(): void {
    this.shiftsIconRenderingHelperService.resetStateForTimeline();
    this.shiftsIconRenderingHelperService.resetStateForUnassignedRegion();
    this.refreshClickSubject.next();
  }

  private async refreshTimeline(): Promise<void> {
    this.store.dispatch(new UpdateWindowWidthHeightToWindowInnerWidthHeight());
    const visibleUserIdsSet = await firstValueFrom(
      this.assignedRegionVisibilityStateService.visibleUserIdsSet$,
    );

    const visibleDateRange = this.store.selectSnapshot(
      ShiftTimelineDataState.visibleDateRange,
    );

    const canRenderStays = await firstValueFrom(
      this.timelineStaysSuggestionService.canRenderStays$,
    );

    if (isValid(visibleDateRange.start) && isValid(visibleDateRange.end)) {
      this.assignedRegionVisibilityStateService.clearAllShiftVisibilityRecord();
      this.timelineAssignedShiftValidationsStateService.clearCache();
      this.timelineUnassignedShiftValidationsStateService.clearCache();
      this.store.dispatch([
        new ClearDeterminedShiftActivities(),
        new ClearShiftActivitiesRows(),
        new ClearUnassignedShiftsWithoutActivities(),
        new ClearAssignmentsFromAllUsers(),
        new ClearAllUnassignedActivitiesRelatedData(),
      ]);

      // Delay updating the kpi settings to reduce main thread load
      setTimeout(async () => {
        await firstValueFrom(
          this.timelineCommonOperationsService.setPreferredSetting(),
        );
      }, 50);

      // Fetch assignments again
      firstValueFrom(
        this.timelineCommonOperationsService.updateUsersAssignmentsState(
          Array.from(visibleUserIdsSet),
        ),
      ).finally(() => {
        this.timelineCommonOperationsService.refreshTotalProductivityAllFilteredUsers(
          true,
        );
        this.timelineCommonOperationsService.reloadVisibleVacationBalances(
          Array.from(visibleUserIdsSet),
        );
        this.timelineCommonOperationsService.refreshKpisForVisibleUsers(true);

        this.timelineCommonOperationsService.refreshUnassignedRegion();

        if (canRenderStays) {
          this.timelineStaysSuggestionService.refreshDeterminedStaysData();
        }
      });
    }
  }

  toggleViewSection(): void {
    this.isViewOptionCardOpen = !this.isViewOptionCardOpen;
    const shouldCloseOtherCards = this.isFilterDrawerVisible()
      ? window.innerWidth < WINDOW_WIDTH_FOR_COLLAPSE_WHEN_FILTER_OPEN
      : window.innerWidth < WINDOW_WIDTH_FOR_COLLAPSE;
    if (shouldCloseOtherCards && this.isViewOptionCardOpen) {
      this.isActionCardOpen = false;
      this.isKPICardOpen = false;
      this.isQuickCreationCardOpen = false;
    }
  }

  toggleActionCard(): void {
    this.isActionCardOpen = !this.isActionCardOpen;
    const shouldCloseOtherCards = this.isFilterDrawerVisible()
      ? window.innerWidth < WINDOW_WIDTH_FOR_COLLAPSE_WHEN_FILTER_OPEN
      : window.innerWidth < WINDOW_WIDTH_FOR_COLLAPSE;
    if (shouldCloseOtherCards && this.isActionCardOpen) {
      this.isViewOptionCardOpen = false;
      this.isQuickCreationCardOpen = false;
    }
    const suggestedShiftTemplates = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.suggestedShiftTemplates,
    );
    if (suggestedShiftTemplates.length) {
      this.isQuickCreationCardOpen = false;
    }
  }

  toggleQuickCreationCard(): void {
    this.isQuickCreationCardOpen = !this.isQuickCreationCardOpen;
    const shouldCloseOtherCards = this.isFilterDrawerVisible()
      ? window.innerWidth < WINDOW_WIDTH_FOR_COLLAPSE_WHEN_FILTER_OPEN
      : window.innerWidth < WINDOW_WIDTH_FOR_COLLAPSE;
    if (shouldCloseOtherCards && this.isQuickCreationCardOpen) {
      this.isViewOptionCardOpen = false;
      this.isActionCardOpen = false;
    }
    const suggestedShiftTemplates = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.suggestedShiftTemplates,
    );
    if (suggestedShiftTemplates.length) {
      this.isActionCardOpen = false;
      this.isKPICardOpen = false;
    }
  }

  toggleKPICard(): void {
    this.isKPICardOpen = !this.isKPICardOpen;
    const shouldCloseOptionsCard = this.isFilterDrawerVisible()
      ? window.innerWidth < WINDOW_WIDTH_FOR_COLLAPSE_WHEN_FILTER_OPEN
      : window.innerWidth < WINDOW_WIDTH_FOR_COLLAPSE;
    if (shouldCloseOptionsCard && this.isKPICardOpen) {
      this.isViewOptionCardOpen = false;
    }
    const suggestedShiftTemplates = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.suggestedShiftTemplates,
    );
    if (suggestedShiftTemplates.length) {
      this.isQuickCreationCardOpen = false;
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
